import { trpc } from '@tencent/tpro_rmonitor_login_server_login_auth';
import { LocationDescriptor, Path } from 'history';
import { isString } from 'lodash-es';
import { observable, makeObservable } from 'mobx';
import { AuthNodes } from '@/config/app/auth';
import { userImplement } from '@/config/hooks/user';
import { isSgp } from '@/infrastructure/auth';
import { TRPCGet } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import {
  getParamFromUrl,
  getHistory,
  toSearchObj, toSearchStr,
} from '@/infrastructure/history';
import { logger } from '@/infrastructure/logger';
import { getPlatform, getProductId, isAndroid, isIOS } from '@/infrastructure/product-info';
import { reporter } from '@/infrastructure/reporter/datong';
import { storageSet } from '@/infrastructure/storage-utils';
import { isDataLabelingPlatform } from '@/infrastructure/utils/issue';
import { Toggle } from '@/presentation/components/toggle/toggle';
import { BUGLY_LOGIN_STORAGE_KEY } from '@/presentation/hooks/user/types';
import { getObjectLabel, IConfigObject, IFilterConfig } from '@/presentation/pages/setting/data-labeling/config';
import { IGetProductExceedRsp, IOverdueDataRecoveryBannerRsp } from '@/proto/resource-manager';

import { filterRuleService } from '@/service/filter-rule';
import { IGetSDKVersionUpgradeTipForProRsp, overviewService } from '@/service/overview';
import { FreeStatus, IProductInfo, ProductInfo, userService } from '@/service/pro-user';
import { productService } from '@/service/product';
import { IGetProductPermissionsRsp, productPermissionsService, ProductType } from '@/service/product-permissions';
import { resourcePackageService } from '@/service/resource-package';
import IUserInfo = trpc.rmonitor.login_server.IUserInfo;

export interface Product {
  productId: string;        // 产品app_id
  appKey: string;           // 产品app_key
  productName: string;      // 产品名称
  owner: string;            // 负责人
  pid: string;              // 平台类型
  logoUrl: string;
  isFlutter: number;
  hasPermission: boolean;
}

// type RawUserInfo = {
//   isSuperUser: boolean;
//   isAuth: boolean;
//   roleId: string | number;
//   roleName: string;
//   rolePermissions: string[];
// };

type RawPerm = {
  isAdmin: boolean,
  permissions: string[],
  roleId: string | number;
};

export class ProductProStore {
  loaded = false;
  products: IProductInfo[] = [];
  product: ProductInfo | undefined = undefined;
  productId = '';                           // 当前选中的平台
  pid = '';                                 // 兼容bugly的pid
  permission = true;                        // 当前选中的产品组的权限
  isFlutter = false;
  isSuperUser = false;

  // 外网版本用
  hasProduct = true;

  // 当前产品用量状态
  productExceed: IGetProductExceedRsp = {};
  // 欠费数据恢复banner条
  overdueDataRecoveryBanner: IOverdueDataRecoveryBannerRsp = {};
  // 过滤黑产
  filterConfig: IFilterConfig | undefined;
  // 合规提醒的banner条
  complianceBanner: IGetSDKVersionUpgradeTipForProRsp = {};

  rawPerm: RawPerm = {
    isAdmin: false,
    permissions: [],
    roleId: '',
  };

  // 当前会员类型对应的权限点
  authorityNodes: string[] = [
    AuthNodes.Launch,
    AuthNodes.CompareMode,
    AuthNodes.LogAnalyze,
    AuthNodes.IssueSplit,
  ];

  // 产品类型: 免费产品，收费产品，SVIP产品
  productType = ProductType.FREE_PRODUCT;

  // 七彩石白名单：gamePerformance 功能是否对当前产品启用
  gamePerformanceToggleEnabled = false;

  public constructor() {
    makeObservable(this, {
      loaded: observable,
      products: observable,
      product: observable,
      productId: observable,
      pid: observable,
      permission: observable,
      isFlutter: observable,
      isSuperUser: observable,
      productExceed: observable,
      rawPerm: observable,
      filterConfig: observable,
      authorityNodes: observable,
      productType: observable,
      overdueDataRecoveryBanner: observable,
      complianceBanner: observable,
      gamePerformanceToggleEnabled: observable,
    });

    const user = userImplement.getUserInfo();
    this.hasProduct = user?.hasProduct ?? true;
  }

  public isFreeProduct = () => this.productType === ProductType.FREE_PRODUCT;
  public isNormalProduct = () => this.productType === ProductType.NORMAL_PRODUCT;
  public isPaidProduct = () => this.productType === ProductType.PAID_PRODUCT;

  public setHasProduct = async (hasProduct: boolean) => {
    this.hasProduct = hasProduct;
  };

  public setPermission = async (permission: boolean) => {
    this.permission = permission;
  };

  /**
   * 加载产品组列表
   */
  public loadProducts = async () => {
    logger.info('ProductStore|loadGroupProducts');
    this.products = await this.getAppList();
  };

  public loadDemoProducts = async () => {
    logger.info('ProductStore|loadDemoProducts');
    this.products = await this.getDemoAppList();
  };

  public setLoaded = async (loaded: boolean) => {
    this.loaded = loaded;
  };

  public getUserInfo = async (productId: string) => await TRPCGet(`/${API_PREFIX}/api/user/info?productId=${productId}`);

  /**
   * 刷新 gamePerformance 七彩石白名单判定
   * 在产品切换（包括首屏初始化）后调用，结果写入 observable，触发菜单重算
   */
  public refreshGamePerformanceToggle = async (productId: string) => {
    if (!productId) {
      this.gamePerformanceToggleEnabled = false;
      return;
    }
    try {
      const toggle = new Toggle();
      const enabled = await toggle.isEnable('gamePerformance', ['appId'], {
        appId: productId,
      });
      if (productId !== this.productId) return;
      this.gamePerformanceToggleEnabled = enabled;
    } catch {
      if (productId !== this.productId) return;
      this.gamePerformanceToggleEnabled = false;
    }
  };

  /**
   * 只设置store的产品id和pid，其他初始化逻辑在useEffect里监听到productID变化时处理
   * @param productId
   * @param pid
   * @param to
   */
  public changeProduct = async (productId: string, pid: string | number, to?: Path | LocationDescriptor<any>) => {
    logger.info('ProductStore|setId, productId:', productId, this.productId);
    this.productId = productId;

    if (to) {
      const history = getHistory();
      const extraParams = {
        productId,
        pid,
      };

      if (isString(to)) {
        history.push({
          pathname: to,
          search: toSearchStr(extraParams),
        });
      } else {
        const { search, ...locationParams } = to;
        const searchObj = toSearchObj(search ?? '');
        history.push({
          ...locationParams,
          search: toSearchStr({
            ...searchObj,
            ...extraParams,
          }),
        });
      }
    }
  };

  public getProductPermissions = async () => {
    if (isSgp()) {
      return {
        productType: ProductType.PAID_PRODUCT,
      };
    }

    return productPermissionsService.getProductPermissions();
  };

  public init = async () => {
    logger.info('ProductStore|init');
    this.loaded = false;

    const initUserTasks: [Promise<IUserInfo | null>, Promise<boolean>] = [
      userService.getUserInfo(),
      userService.checkSuperAdmin({}),
    ];
    const [userInfo, isSuperUser] = await Promise.all(initUserTasks);
    this.isSuperUser = isSuperUser;
    const hasProduct = userInfo?.hasProduct ?? false;
    this.hasProduct = isSuperUser ? true : hasProduct;

    storageSet(BUGLY_LOGIN_STORAGE_KEY, JSON.stringify(userInfo));
    const urlProductId = getParamFromUrl('productId');
    const productId = urlProductId;

    if (productId) {
      const getAppInfoTasks: [
        Promise<ProductInfo | undefined>,
        Promise<boolean>,
        Promise<boolean>,
        Promise<IGetProductPermissionsRsp>,
      ] = [
        this.getAppInfoById(productId),
        userService.checkSuperAdmin({}),
        userService.checkIsAdmin({ productId }),
        this.getProductPermissions(),
      ];

      const [info, isSuperUser, isAdmin, productPermission] = await Promise.all(getAppInfoTasks);

      this.product = info;
      this.productId = info?.productId || productId;
      this.pid = info?.platformId || '0';
      this.isFlutter = !!info?.isFlutter;

      this.permission = info?.hasPermission || false;
      // 体验产品，过期按无权限处理
      if (info?.productType === ProductType.FREE_PRODUCT
        && info?.freeStatus === FreeStatus.Expired) {
        this.permission = false;
      }
      // 超管
      if (isSuperUser) {
        this.permission = true;
      }

      this.rawPerm = {
        ...this.rawPerm,
        isAdmin,
      };

      if (productPermission.productType) {
        this.productType = productPermission.productType;
      }

      // 没权限执行
      if (this.permission) {
        await this.setFilterConfig(productId, this.pid);

        // 设置上报的产品信息
        reporter?.setPublicParams?.({
          productId,
          productName: this.product?.productName,
        });
      }
    }

    await this.refreshGamePerformanceToggle(this.productId);

    this.loaded = true;
  };

  public setExceed = async () => {
    this.productExceed = await resourcePackageService.getProductExceed({});
  };

  public setOverdueDataRecoveryBanner = async () => {
    if (isSgp()) return;

    this.overdueDataRecoveryBanner = await resourcePackageService.getOverdueDataRecoveryBanner({
      productId: getProductId(),
    });
  };

  public setComplianceBanner = async () => {
    if (isSgp()) return;

    if (isAndroid() || isIOS()) {
      this.complianceBanner = await overviewService.getSDKVersionUpgradeTipForPro({
        productId: getProductId(),
        platform: getPlatform(),
      });
    }
  };

  public setFilterConfig = async (productId: string, pid?: string) => {
    if (!isDataLabelingPlatform()) {
      return;
    }
    const config =  await filterRuleService.getConfig({
      productId,
      platformId: Number(pid),
    });
    const filterRules = await filterRuleService.getFilterRules({
      productId,
    });
    const configObject: Record<string, IConfigObject> = {};
    config.monitorItems?.forEach((item) => {
      if (item.monitorKey) {
        configObject[item.monitorKey] = {
          values: filterRules?.filter(rulesItem => rulesItem.monitorKey === item.monitorKey)
            ?.map(item => ({ value: item.labelKey || '', label: item.labelName || '' })) || [],
          labelOptions: getObjectLabel(item.monitorKey, config.labels) || [],
        };
      }
    });
    this.filterConfig = {
      config,
      configObject,
      filterRules,
    };
  };

  private getAppList = async (): Promise<IProductInfo[]> => {
    try {
      const res = await userService.getProductList();
      return res.map(product => ({
        ...product,
        hasPermission: true,
      }));
    } catch (e) {
      logger.error('getAppList:', e);
      return [];
    }
  };

  private getDemoAppList = async (): Promise<IProductInfo[]> => {
    try {
      const res = await userService.getDemoProductList({});
      return res.map(product => ({
        ...product,
        hasPermission: true,
      }));
    } catch (e) {
      logger.error('getDemoAppList:', e);
      return [];
    }
  };

  private getAppInfoById = async (productId: string): Promise<ProductInfo | undefined> => {
    try {
      const hasPermission = await userService.checkUserInProduct({ productId });
      const data = await productService.getProductInfo({ productId }, {
        ignoreError: true,
      });
      return {
        isFlutter: !!data.isFlutter,
        productId: data?.productId || productId,
        appKey: data?.appKey || '',
        productName: data?.productName || '',
        platformId: `${data?.platformId || 0}`,
        owner: data?.creator || '',
        hasPermission,
        expireTime: '',
        freeStatus: data.freeStatus as FreeStatus,
        expireDate: data.expireDate as string,
        type: data?.type ?? undefined,
        productType: data.productType as ProductType,
        diagnoseGuidMappingField: data?.diagnoseGuidMappingField,
      };
    } catch (e) {
      logger.error('getAppInfoById:', e);
      return undefined;
    }
  };
}
